import {
  AfterContentChecked,
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  HostListener,
  input,
  OnChanges,
  OnDestroy,
  OnInit,
  output,
  Signal,
  SimpleChanges,
  viewChild,
} from '@angular/core';
import { isEqual } from '@maplecroft-typescript/utils';
import { NgbDropdown } from '@ng-bootstrap/ng-bootstrap';
import { Subscription } from 'rxjs';
import { GeneralLoadedContent, INITIAL_GENERAL_LOADING_CONTENT, LoadingRequestStatus } from '../../../shared-models';
import { SearchInputComponent } from '../../search-input';
import { QuickSearchDropdownContent, QuickSearchDropdownItemType } from '../models';
import { QuickSearchDropdownResultsComponent } from '../quick-search-dropdown-results/quick-search-dropdown-results.component';

@Component({
  selector: 'grid-ui-quick-search-dropdown',
  templateUrl: './quick-search-dropdown.component.html',
  styleUrls: ['./quick-search-dropdown.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class QuickSearchDropdownComponent implements OnInit, OnChanges, OnDestroy, AfterContentChecked {
  /** Whether to allow a null item in the dropdown */
  public readonly allowNullItem = input<boolean>(false);

  /** The label to be shown in the header of the dropdown */
  public readonly countHeaderLabel = input<string>();

  /** The data to be displayed in the dropdown */
  public readonly dropdownData = input<GeneralLoadedContent<QuickSearchDropdownContent>>(INITIAL_GENERAL_LOADING_CONTENT);

  /** The debounce time in ms to be passed to grid-ui-search-input */
  public readonly debounce = input<number>(200);

  /** Id to be assigned to the parent dropdown div element */
  public readonly dropdownId = input<string>();

  /** Message to show on the dropdown button */
  public readonly dropdownMessage = input<string>();

  /** Whether the dropdown is invalid */
  public readonly invalid = input<boolean>(false);

  /** Placeholder text for search input box */
  public readonly placeholder = input<string | undefined>();

  /** Whether the dropdown is disabled */
  public readonly disabled = input<boolean>(false);

  /** Whether to show the search box or not */
  public readonly searchable = input<boolean>(true);

  /** Fires when the user has changed the search term */
  public readonly hintChange = output<string>();

  /** Fires when the dropdown is opened */
  public readonly opened = output<void>();

  /**
   * Fires when the user clicks or presses enter on a result and
   * emits the Country Risk Index id or
   * Country/Site geo_id of the selected result.
   */
  public readonly resultSelected = output<QuickSearchDropdownItemType>();

  public readonly dropdown = viewChild.required(NgbDropdown);
  public readonly searchInputContainer = viewChild<ElementRef<HTMLDivElement>>('searchInputContainer');
  public readonly searchInputComponent = viewChild(SearchInputComponent);
  public readonly resultsComponent = viewChild(QuickSearchDropdownResultsComponent);

  public activeIndex = -1;
  public errorPrimaryAction = 'Try Again';
  public hint = '';

  public readonly RequestStatus = LoadingRequestStatus;

  private dropdownChangeSubscription!: Subscription;
  private pendingToNotifyOpen = false;

  protected readonly countHeader = computed(() => {
    const label = this.countHeaderLabel();
    if (!label) {
      return null;
    }

    const count = this.dropdownData().content?.items?.length || 0;
    return `${count} ${label}`;
  });

  protected readonly items: Signal<QuickSearchDropdownItemType[]> = computed(() => {
    const data = this.dropdownData();
    const items = data.content?.items || [];
    const nullItem = { name: '--Empty--', isHeader: false, isDisabled: false };
    const hasNullItem = items.find((item) => isEqual(item, nullItem));

    if (this.allowNullItem() && !hasNullItem && !this.hint.length) {
      items.unshift(nullItem);
    }

    return items;
  });

  public ngOnInit(): void {
    this.dropdownChangeSubscription = this.dropdown().openChange.subscribe((change: boolean) => {
      this.pendingToNotifyOpen = change;
      if (change === true && !this.dropdownData().content?.items?.length) {
        this.opened.emit();
      }

      this.clearSearchTerm();
    });
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (
      Object.prototype.hasOwnProperty.call(changes, 'requestStatus') &&
      !changes.requestStatus.firstChange &&
      changes.requestStatus.currentValue !== LoadingRequestStatus.loading
    ) {
      this.pendingToNotifyOpen = true;
    }
    if (Object.prototype.hasOwnProperty.call(changes, 'results')) {
      this.activeIndex = -1;
    }
  }

  public ngOnDestroy(): void {
    if (this.dropdownChangeSubscription && !this.dropdownChangeSubscription.closed) {
      this.dropdownChangeSubscription.unsubscribe();
    }
  }

  /**
   * The openChange event from NgbDropdown is fired before the dropdown is actually
   * open, so we can't give focus to the grid-ui-search-input as it's still hidden.
   * Here we wait for the grid-ui-search-input container to become visible to change
   * the send an event to SearchInputComponent.
   */
  public ngAfterContentChecked(): void {
    const searchInputContainer = this.searchInputContainer();

    if (this.pendingToNotifyOpen && this.searchable() && searchInputContainer?.nativeElement.offsetParent && this.searchInputComponent()) {
      searchInputContainer.nativeElement.focus();
      this.pendingToNotifyOpen = false;
    }
  }

  public reloadWithHint(value: string): void {
    this.hint = value;
    this.hintChange.emit(value);
  }

  public selectedResultByClick(item: QuickSearchDropdownItemType): void {
    this.resultSelected.emit(item);
    this.clearSearchTerm();
    this.dropdown().close();
  }

  public setActive(index: number): void {
    this.activeIndex = index;
  }

  @HostListener('keydown', ['$event'])
  public keyEvent(event: KeyboardEvent): void {
    const dropdownDataContent = this.dropdownData().content;

    if (dropdownDataContent) {
      let result: any;
      switch (event.key) {
        case 'ArrowDown':
        case 'Down':
          event.preventDefault();
          this.activeIndex = this.activeIndex >= dropdownDataContent.items.length - 1 ? 0 : this.activeIndex + 1;
          this.resultsComponent()?.scrollToIndex(this.activeIndex);
          break;
        case 'ArrowUp':
        case 'Up':
          event.preventDefault();
          this.activeIndex = this.activeIndex > 0 ? this.activeIndex - 1 : dropdownDataContent.items.length - 1;
          this.resultsComponent()?.scrollToIndex(this.activeIndex);
          break;
        case 'Enter':
          event.preventDefault();

          if (this.activeIndex === -1) {
            return;
          }

          result = dropdownDataContent.items[this.activeIndex];
          this.resultSelected.emit(result);
          this.dropdown().close();

          break;
      }
    }
  }

  private clearSearchTerm() {
    if (this.searchable()) {
      this.searchInputComponent()?.clearSearchTerm();
    }
  }
}
